import React from 'react';
import cn from 'classnames';
import { bool, func, node, number, object, oneOf, oneOfType, string } from 'prop-types';
import Toggler from '@fiverr-private/ui_toggle';
import { getContext } from '@fiverr-private/fiverr_context';
import getRenderable from '../../../lib/getRenderable';
import { HOVER_TIMEOUT, POSITIONS, TRIGGERS, UNSUPPORTED_TRIGGER_ERROR } from './constants';
import Box from './Box';
import classes from './index.module.scss';

const isFunction = (fn) => typeof fn === 'function';

/**
 * Determines if a given position is considered as `Horizontal` position
 * @param {String} position - The popover position
 * @returns {Boolean}
 */
const isHorizontalPosition = (position) => {
    const [basePosition] = position.split('-');

    return [POSITIONS.LEFT, POSITIONS.RIGHT].includes(basePosition);
};

/**
 * Returns the enforced popover position under the current device restrictions
 * @param {String} position - The popover position
 * @returns {String}
 */
const getEnforcedPosition = (position) => {

    // Mobile horizontal position will be enforced to top
    const shouldEnforcePosition = getContext().isMobile && isHorizontalPosition(position);
    return shouldEnforcePosition ? POSITIONS.TOP : position;
};

/**
 * @property {Object} popoverProps The relevant props passed to the component to support render props.
 * @property {Object} eventHandlers The events this popover responds to.
 * @property {String} position The popover's calculated position. Takes into consideration screen overflowing and will try to render the content in the opposite direction.
 * @deprecated Please use Popover from @fiverr-private/popover instead.
 */
class Popover extends Toggler {
    constructor(props) {
        super(props);

        /**
         * Merge the state with Toggler's base state, but first make sure "state" is an object.
         */
        this.state = this.state || {};

        Object.assign(this.state, {
            container: null
        });

        this.mouseLeaveTimeout = null;
        this.openDelayTimeout = null;

        this.cancelMouseLeave = this.cancelMouseLeave.bind(this);
        this.setContentRef = this.setContentRef.bind(this);
        this.getCustomStyles = this.getCustomStyles.bind(this);
    }

    componentWillUnmount() {
        const { container } = this.state;
        const { appendToBody } = this.props;

        this.clearDelayOpen();
        this.cancelMouseLeave();

        if (!container) { return; }

        if (appendToBody) {
            document.body.removeChild(container);
        } else {
            this.anchorElement.parentNode.removeChild(container);
        }

    }

    get popoverProps() {
        const {
            open,
            close,
            toggle,
            preventCloseAction
        } = this;

        const {
            isOpen
        } = this.state;

        const {
            trigger,
            children,
            ...props
        } = this.props;

        return Object.assign({
            open,
            close,
            toggle,
            preventCloseAction,
            isOpen
        }, props);
    }

    get eventHandlers() {
        const {
            trigger
        } = this.props;
        const {
            isOpen
        } = this.state;

        switch (trigger) {
            case TRIGGERS.CLICK:
                return {
                    onClick: this.onClick
                };
            case TRIGGERS.HOVER:
                return {
                    onMouseEnter: (...args) => {
                        this.cancelMouseLeave();
                        !isOpen && this.open(...args);
                    },
                    onMouseLeave: () => {
                        this.clearDelayOpen();
                        this.mouseLeaveTimeout = setTimeout(this.close, HOVER_TIMEOUT);
                    }
                };
            default:
                throw new Error(UNSUPPORTED_TRIGGER_ERROR);
        }
    }

    get wrapperClasses() {
        const {
            trigger,
            className,
            openClassName,
            appendToBody
        } = this.props;
        const { isOpen } = this.state;

        return cn(
            classes.popover,
            { [classes.clickable]: trigger === TRIGGERS.CLICK },
            { [classes.inlineAppend]: !appendToBody },
            className,
            { [openClassName]: openClassName && isOpen }
        );
    }

    get containerClasses() {
        const {
            containerClassName,
            className
        } = this.props;

        return cn(
            containerClassName,

            // This should be removed in FIT6. It's kept here since otherwise it's a breaking change
            className,
            classes.popoverContainer
        );
    }

    onClick = (e) => {
        if (this.openDelayTimeout) {
            return;
        }

        this.toggle(e);
    }

    generateContainer() {
        if (this.state.container) { return; }

        const { appendToBody } = this.props;
        const container = document.createElement('div');
        container.className = this.containerClasses;

        if (appendToBody) {
            document.body.appendChild(container);
        } else {
            this.anchorElement.parentNode.appendChild(container);
        }

        this.setState({
            container
        });
    }

    cancelMouseLeave() {
        clearTimeout(this.mouseLeaveTimeout);
    }

    clearDelayOpen() {
        clearTimeout(this.openDelayTimeout);
        this.openDelayTimeout = null;
    }

    getCustomStyles() {
        const {
            style,
            maxWidth
        } = this.props;

        return maxWidth ? { ...style, maxWidth } : style;
    }

    setContentRef(element) {
        const { isOpen } = this.state;
        const {
            contentRef
        } = this.props;

        this.anchorElement = element;

        isOpen && this.generateContainer();

        contentRef(element);
    }

    delayOpen = (delay, e) => new Promise((resolve) => {
        if (!e.target) {
            resolve();
            return;
        }

        this.openDelayTimeout = setTimeout(() => {
            resolve();
            this.clearDelayOpen();
        }, delay);
    });

    async togglerWillOpen(e) {
        const { willOpen, delay } = this.props;

        this.generateContainer();

        if (delay) {
            await this.delayOpen(delay, e);
        }

        if (isFunction(willOpen)) {
            await willOpen(e);
        }
    }

    togglerDidOpen(e) {
        const { onOpen } = this.props;

        if (isFunction(onOpen)) {
            onOpen(e);
        }
    }

    async togglerWillClose(e) {
        const { willClose } = this.props;

        if (isFunction(willClose)) {
            await willClose(e);
        }
    }

    togglerDidClose(e) {
        const { onClose } = this.props;

        if (isFunction(onClose)) {
            onClose(e);
        }
    }

    render() {
        const { eventHandlers } = this;
        const {
            children,
            contentClassName,
            content,
            position,
            allowOverflow,
            fullWidth,
            boxFitContent,
            boxClassName,
            boxContentClassName,
            appendToBody,
            trigger,
            withAnimation,
            adjustPositionAfterMillis
        } = this.props;
        const {
            isOpen,
            container
        } = this.state;

        const { isMobile } = getContext();

        const contentClasses = cn(
            classes.content,
            contentClassName
        );
        const enforcedPosition = getEnforcedPosition(position);

        return (
            <div className={this.wrapperClasses}
                ref={this.preventCloseAction}>
                <span className={contentClasses}
                    ref={this.setContentRef}
                    {...eventHandlers}>
                    {getRenderable(children, this.popoverProps)}
                </span>
                <Box isOpen={isOpen}
                    className={boxClassName}
                    contentClassName={boxContentClassName}
                    style={this.getCustomStyles()}
                    container={container}
                    anchor={this.anchorElement}
                    position={enforcedPosition}
                    allowOverflow={allowOverflow}
                    fullWidth={isMobile && fullWidth}
                    boxFitContent={boxFitContent}
                    onMouseEnter={this.cancelMouseLeave}
                    onMouseLeave={eventHandlers.onMouseLeave}
                    preventCloseAction={this.preventCloseAction}
                    appendToBody={appendToBody}
                    trigger={trigger}
                    withAnimation={withAnimation}
                    adjustPositionAfterMillis={adjustPositionAfterMillis}>
                    { getRenderable(content, this.popoverProps) }
                </Box>
            </div>
        );
    }
}

Popover.displayName = 'Popover';

Popover.propTypes = {

    /**
     * The content that's being wrapped with the popover functionality.
     */
    children: node.isRequired,

    /**
     * The popover box's content.
     * Whatever you pass here will get the popover's internal props.
     */
    content: node.isRequired,

    /**
     * The direction in which the popover box will open.
     * Supported options are exposed through the static `POSITIONS` property. | `POSITIONS.TOP`
     * Mobile devices will enforce `POSITIONS.TOP` direction when horizontal directions is used, i.e right / left positions.
     */
    position: oneOf(Object.values(POSITIONS)),

    /**
     * Whether or not an overflow is allowed.
     * If true, will preserve the original position even if the box overflows the screen.
     */
    allowOverflow: bool,

    /**
     * The triggering behaviour for the popover.
     * Supported options are exposed through the static `TRIGGERS` property.
     */
    trigger: oneOf(Object.values(TRIGGERS)),

    /**
     * A ref function for the content element.
     */
    contentRef: func,

    /**
     * Custom classes you wish to pass to the component.
     * Please note that these classes get passed to both the trigger and the wrapper for the popover box.
     */
    className: string,

    /**
     * Custom classes you wish to pass to the component's trigger element.
     */
    contentClassName: string,

    /**
     * Custom classes you wish to pass to the component's trigger element when the popover is open.
     */
    openClassName: string,

    /**
     * Custom classes you wish to pass to the wrapper of the component's popover box element.
     * Please note that this element will also receive any classes you have specified in the `className` prop.
     */
    containerClassName: string,

    /**
     * Custom classes you wish to pass to the component's popover box element.
     */
    boxClassName: string,

    /**
     * Custom classes you wish to pass to the component's popover box content element.
     */
    boxContentClassName: string,

    /**
     * Custom styles you wish to pass to the component.
     */
    style: object,

    /**
     * Whether the popover box should take the full screen width or not.
     * This will only apply when the user agent device type is `mobile`.
     */
    fullWidth: bool,

    /**
     * Whether the popover box should take at least the width of the content.
     */
    boxFitContent: bool,

    /**
     * A callback function triggered when the popover is about to be opened.
     * Receives one argument - the event that caused the open cycle.
     */
    willOpen: func,

    /**
     * A callback function triggered when the popover is about to be closed.
     * Receives one argument - the event that caused the close cycle.
     */
    willClose: func,

    /**
     * The number of milliseconds until the popover will opened
     */
    delay: number,

    /**
     * A callback function triggered when the popover is opened.
     * Receives one argument - the event that caused the open cycle.
     */
    onOpen: func,

    /**
     * A callback function triggered when the popover is closed.
     * Receives one argument - the event that caused the close cycle.
     */
    onClose: func,

    /**
     * Popover open by default.
     */
    defaultOpen: bool,

    /**
     * Whether the component's popover box should be appended to the body.
     * Setting this to false will cause the popover box to be appended next to the trigger instead.
     */
    appendToBody: bool,

    /**
     * The max-width of the popover box.
     */
    maxWidth: oneOfType([number, string]),

    /**
     * Whether the component's popover box should be animated.
     */
    withAnimation: bool
};

Popover.defaultProps = {
    position: POSITIONS.TOP,
    allowOverflow: false,
    fullWidth: false,
    boxFitContent: true,
    trigger: TRIGGERS.CLICK,
    style: {},
    contentRef: Function.prototype,
    delay: 0,
    defaultOpen: false,
    appendToBody: true,
    withAnimation: false
};

Object.assign(Popover, {
    POSITIONS,
    TRIGGERS
});

export default Popover;
